import { useState, useEffect, useRef, useMemo } from "react";
import {
  Modal,
  TextInput,
  Table,
  Text,
  Badge,
  Stack,
  Group,
  ScrollArea,
  Box,
  Button,
  SegmentedControl,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconSearch, IconPlus, IconFlask } from "@tabler/icons-react";
import CreateMacroModal from "./CreateMacroModal";

interface DiagnosisMacro {
  id: string;
  code: string;
  label: string;
  diagnosisText: string;
  microscopicDescription: string | null;
  category: string | null;
  cptCode: string | null;
  icdCode: string | null;
  icdCodes?: string[];
  scope?: "global" | "personal";
  createdById?: string | null;
  linkedStains?: Array<{
    id: string;
    code: string;
    description: string;
    stainType: string;
  }>;
}

interface MacroSearchModalProps {
  opened: boolean;
  onClose: () => void;
  macros: DiagnosisMacro[];
  variableMap?: Record<string, string | null | undefined>;
}

function stripHtmlAndVars(text: string): string {
  if (!text) return "";
  const withoutVars = text
    .replace(/\{\{[^}]+\}\}/g, "")
    .replace(/\[\[[^\]]+\]\]/g, "");
  const doc = new DOMParser().parseFromString(withoutVars, "text/html");
  return doc.body.textContent || "";
}

function resolveDisplayVariables(
  text: string,
  vars: Record<string, string | null | undefined>
): string {
  if (!text) return "";
  let result = text.replace(/\{\{(\w+)\}\}/g, (match, name) => {
    const val = vars[name];
    return val?.trim() ? val.trim() : match;
  });
  result = result.replace(/\[\[(\w+)\]\]/g, (match, name) => {
    const val = vars[name] ?? vars[name.toLowerCase()];
    return val?.trim() ? val.trim() : match;
  });
  return result;
}

const getCategoryColor = (category: string | null) => {
  switch (category?.toLowerCase()) {
    case "benign":
      return "green";
    case "malignant":
      return "red";
    case "dysplasia":
    case "low_grade_dysplasia":
    case "high_grade_dysplasia":
      return "orange";
    case "inflammation":
      return "yellow";
    default:
      return "gray";
  }
};

const formatCategory = (category: string | null) => {
  if (!category) return "—";
  return category.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

export default function MacroSearchModal({
  opened,
  onClose,
  macros,
  variableMap = {},
}: MacroSearchModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [scopeFilter, setScopeFilter] = useState<"all" | "personal" | "global">(
    "all"
  );
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [createOpened, { open: openCreate, close: closeCreate }] =
    useDisclosure(false);

  const existingCodes = useMemo(() => macros.map((m) => m.code), [macros]);

  const filteredMacros = useMemo(() => {
    let result = macros;

    if (scopeFilter !== "all") {
      result = result.filter((m) => (m.scope || "global") === scopeFilter);
    }

    if (!searchQuery.trim()) {
      return result;
    }
    const query = searchQuery.toLowerCase();
    return result.filter((m) => {
      const diagnosisPlain = stripHtmlAndVars(m.diagnosisText).toLowerCase();
      const microscopicPlain = stripHtmlAndVars(
        m.microscopicDescription || ""
      ).toLowerCase();
      return (
        m.code.toLowerCase().includes(query) ||
        diagnosisPlain.includes(query) ||
        microscopicPlain.includes(query) ||
        (m.category && m.category.toLowerCase().includes(query))
      );
    });
  }, [macros, searchQuery, scopeFilter]);

  useEffect(() => {
    if (opened) {
      setSearchQuery("");
      setScopeFilter("all");
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [opened]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Group gap="xs">
            <IconSearch size={18} />
            <Text fw={600}>Search Diagnosis Macros</Text>
          </Group>
        }
        size="90%"
        styles={{ content: { maxWidth: 1400 } }}
        centered
        onKeyDown={handleKeyDown}
      >
        <Stack gap="sm">
          <Group gap="sm" align="flex-end">
            <TextInput
              ref={searchInputRef}
              placeholder="Search by code, diagnosis, or microscopic description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftSection={<IconSearch size={16} />}
              autoFocus
              style={{ flex: 1 }}
            />
            <Button
              size="sm"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={openCreate}
            >
              Add Macro
            </Button>
          </Group>

          <Group justify="space-between" align="center">
            <SegmentedControl
              value={scopeFilter}
              onChange={(v) =>
                setScopeFilter(v as "all" | "personal" | "global")
              }
              data={[
                { label: "All", value: "all" },
                { label: "My Macros", value: "personal" },
                { label: "Global", value: "global" },
              ]}
              size="xs"
            />
            <Text size="xs" c="dimmed">
              Showing {filteredMacros.length} of {macros.length} macros
            </Text>
          </Group>

          <Box
            style={{
              border: "1px solid var(--mantine-color-default-border)",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <Table
              withColumnBorders
              style={{ tableLayout: "fixed", width: "100%" }}
            >
              <Table.Thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  background: "var(--mantine-color-body)",
                  boxShadow: "0 1px 0 var(--mantine-color-default-border)",
                }}
              >
                <Table.Tr>
                  <Table.Th style={{ width: 70 }}>Code</Table.Th>
                  <Table.Th style={{ width: "24%" }}>Diagnosis</Table.Th>
                  <Table.Th>Microscopic Description</Table.Th>
                  <Table.Th style={{ width: 120 }}>Linked Stains</Table.Th>
                  <Table.Th style={{ width: 100 }}>Category</Table.Th>
                  <Table.Th style={{ width: 70 }}>CPT</Table.Th>
                  <Table.Th style={{ width: 80 }}>ICD</Table.Th>
                  <Table.Th style={{ width: 100 }}>Scope</Table.Th>
                </Table.Tr>
              </Table.Thead>
            </Table>
            <ScrollArea h={480}>
              <Table
                withColumnBorders
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <colgroup>
                  <col style={{ width: 70 }} />
                  <col style={{ width: "24%" }} />
                  <col />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 70 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 100 }} />
                </colgroup>
                <Table.Tbody>
                  {filteredMacros.length > 0 ? (
                    filteredMacros.map((macro) => {
                      const resolvedDiagnosis = resolveDisplayVariables(
                        macro.diagnosisText,
                        variableMap
                      );
                      const resolvedMicroscopic = resolveDisplayVariables(
                        macro.microscopicDescription || "",
                        variableMap
                      );
                      const isPersonal =
                        (macro.scope || "global") === "personal";
                      return (
                        <Table.Tr key={macro.id}>
                          <Table.Td>
                            <Text fw={700} ff="monospace" size="sm" c="blue">
                              {macro.code}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Box
                              style={{
                                fontSize: "var(--mantine-font-size-sm)",
                              }}
                              dangerouslySetInnerHTML={{
                                __html: resolvedDiagnosis,
                              }}
                            />
                          </Table.Td>
                          <Table.Td>
                            {resolvedMicroscopic ? (
                              <Box
                                style={{
                                  fontSize: "var(--mantine-font-size-sm)",
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: resolvedMicroscopic,
                                }}
                              />
                            ) : (
                              <Text size="sm" c="dimmed">
                                —
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            {macro.linkedStains &&
                            macro.linkedStains.length > 0 ? (
                              <Tooltip
                                label={macro.linkedStains
                                  .map((s) => `${s.code} — ${s.description}`)
                                  .join("\n")}
                                multiline
                                maw={300}
                                withArrow
                              >
                                <Group gap={4}>
                                  <IconFlask
                                    size={12}
                                    color="var(--mantine-color-teal-6)"
                                  />
                                  <Text size="xs" c="teal" fw={500}>
                                    {macro.linkedStains.length} stain
                                    {macro.linkedStains.length !== 1 ? "s" : ""}
                                  </Text>
                                </Group>
                              </Tooltip>
                            ) : (
                              <Text size="xs" c="dimmed">
                                —
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              size="sm"
                              color={getCategoryColor(macro.category)}
                              variant="light"
                            >
                              {formatCategory(macro.category)}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              {macro.cptCode || "—"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              {(macro.icdCodes?.length
                                ? macro.icdCodes.join(", ")
                                : macro.icdCode) || "—"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              size="xs"
                              variant="dot"
                              color={isPersonal ? "violet" : "gray"}
                            >
                              {isPersonal ? "Mine" : "Global"}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })
                  ) : (
                    <Table.Tr>
                      <Table.Td colSpan={8}>
                        <Box p="xl" ta="center">
                          <Text c="dimmed">
                            No macros found matching &quot;{searchQuery}&quot;
                          </Text>
                        </Box>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Box>
        </Stack>
      </Modal>

      <CreateMacroModal
        opened={createOpened}
        onClose={closeCreate}
        existingCodes={existingCodes}
      />
    </>
  );
}
